import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import config from '../config';
import i18n from '../i18n';

const tr = i18n.t.bind(i18n);
const API = (p) => `${config.API_BASE_URL}${p}`;
const eur = (c) => (Number(c || 0) / 100).toFixed(2) + '€';

const OrdersReportsPage = ({ embedded = false }) => {
  const [available, setAvailable] = useState(true);
  const [rows, setRows] = useState([]);
  const [days, setDays] = useState(14);

  const load = useCallback(async (d) => {
    try {
      const { data } = await axios.get(API('/ordering/reports/sales'), { params: { days: d } });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setRows([]);
      if (e?.response?.status === 403) setAvailable(false);
    }
  }, []);
  useEffect(() => { load(days); }, [days, load]);

  const { daily, categories, totals } = useMemo(() => {
    const byDay = new Map();
    const byCat = new Map();
    const ordersByDay = new Map();
    rows.forEach((r) => {
      const day = String(r.day).slice(0, 10);
      byDay.set(day, (byDay.get(day) || 0) + Number(r.revenue_cents));
      byCat.set(r.category, (byCat.get(r.category) || 0) + Number(r.revenue_cents));
      ordersByDay.set(day, Math.max(ordersByDay.get(day) || 0, 0) + Number(r.orders_count));
    });
    const daily = [...byDay.entries()]
      .map(([day, cents]) => ({ day: day.slice(5), revenue: +(cents / 100).toFixed(2) }))
      .reverse();
    const categories = [...byCat.entries()]
      .map(([name, cents]) => ({ name, cents }))
      .sort((a, b) => b.cents - a.cents);
    const revenue = [...byDay.values()].reduce((s, v) => s + v, 0);
    return {
      daily, categories,
      totals: {
        revenue,
        days: byDay.size,
        avgPerDay: byDay.size ? Math.round(revenue / byDay.size) : 0,
      },
    };
  }, [rows]);

  const Tile = ({ label, value }) => (
    <div style={{ border: '1px solid #ddd', borderRadius: 10, padding: '10px 16px', minWidth: 140 }}>
      <div style={{ fontSize: 12, opacity: 0.65 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
    </div>
  );

  if (embedded && !available) return null;
  return (
    <div style={{ width: embedded ? '100%' : '96%', margin: embedded ? '30px 0 10px' : '10px auto' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 style={{ margin: '6px 0' }}>📊 {tr('Sales')} — {tr('Orders')}</h2>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}
          style={{ padding: '6px 8px', borderRadius: 6 }}>
          <option value={7}>{tr('Last {{n}} days', { n: 7 })}</option>
          <option value={14}>{tr('Last {{n}} days', { n: 14 })}</option>
          <option value={30}>{tr('Last {{n}} days', { n: 30 })}</option>
          <option value={90}>{tr('Last {{n}} days', { n: 90 })}</option>
        </select>
        {!embedded && <Link to="/orders" className="rlToolbarBtn" style={{ marginLeft: 'auto' }}>{tr('Orders')}</Link>}
      </div>

      <div style={{ display: 'flex', gap: 10, margin: '12px 0', flexWrap: 'wrap' }}>
        <Tile label={tr('Revenue')} value={eur(totals.revenue)} />
        <Tile label={tr('Days with sales')} value={totals.days} />
        <Tile label={tr('Avg / day')} value={eur(totals.avgPerDay)} />
      </div>

      {daily.length === 0 ? (
        <p style={{ opacity: 0.6 }}>{tr('No closed orders in this period yet.')}</p>
      ) : (
        <>
          <h4>{tr('Revenue per day')} (€)</h4>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip formatter={(v) => `${v}€`} />
                <Legend />
                <Bar dataKey="revenue" name={tr('Revenue')} fill="#198754" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <h4 style={{ marginTop: 18 }}>{tr('Revenue per category')}</h4>
          <table style={{ borderCollapse: 'collapse', fontSize: 14, minWidth: 320 }}>
            <tbody>
              {categories.map((c) => (
                <tr key={c.name} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '5px 14px 5px 2px' }}>{c.name}</td>
                  <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600 }}>{eur(c.cents)}</td>
                  <td style={{ padding: '5px 8px', width: 220 }}>
                    <div style={{
                      height: 10, borderRadius: 5, background: '#198754',
                      width: `${Math.max(3, Math.round(100 * c.cents / (categories[0]?.cents || 1)))}%`,
                    }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default OrdersReportsPage;
